import React, { useState, useEffect, useMemo, useRef } from 'react';
import { MultiSelect } from 'primereact/multiselect';
import { InputText } from 'primereact/inputtext';
import { useGetChassisDropdownListQuery } from '../../api';
import { useLanguage } from '../../contexts/LanguageContext';

const normalizeText = (str) =>
    (str || '')
        .replace(/\s+/g, ' ')
        .replace(/\u00A0/g, ' ')
        .trim();

const formatLicensePlate = (plate) => {
    if (!plate) return '';
    return plate.replace(/\s+/g, '').toUpperCase();
};

const ChassisMultiSelect = ({ 
    value, 
    onChange, 
    className = '', 
    disabled = false, 
    showDescription = true,
    placeholder
}) => {
    const { getCurrentLanguageId, th } = useLanguage();
    const [searchText, setSearchText] = useState('');
    const [filterValue, setFilterValue] = useState('');
    const debounceTimerRef = useRef(null);

    const languageId = getCurrentLanguageId();

    const formattedSearchText = useMemo(() => {
        return formatLicensePlate(searchText);
    }, [searchText]);

    const { data: chassisList = [], isLoading: isLoadingChassis } = useGetChassisDropdownListQuery({
        RowCount: 200,
        SearchText: formattedSearchText,
        languageId: languageId
    });

    const selectedChassisIds = useMemo(() => {
        if (!value) return [];
        if (Array.isArray(value)) {
            return value;
        }
        if (typeof value === 'string') {
            return [value];
        }
        return [];
    }, [value]);

    // ÖNEMLİ: API'den gelen options + seçili olanları birleştir
    const chassisOptions = useMemo(() => {
        const apiOptions = chassisList.map((chassis) => ({
            id: chassis.id,
            text: chassis.id,
            rawText: normalizeText(chassis.translatedText),
            licensePlate: chassis.licensePlate,
            fullData: chassis,
            label: chassis.id,
            value: chassis.id
        }));

        // Seçili şasileri ekle (eğer API'de yoklarsa)
        const selectedOptions = selectedChassisIds
            .filter(id => !apiOptions.some(opt => opt.id === id))
            .map(id => ({
                id: id,
                text: id,
                rawText: '',
                licensePlate: '',
                fullData: null,
                label: id,
                value: id
            }));

        return [...selectedOptions, ...apiOptions];
    }, [chassisList, selectedChassisIds]);

    const handleFilterInputChange = (inputValue) => {
        setFilterValue(inputValue);

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
            setSearchText(inputValue);
        }, 100);
    };

    const filterTemplate = () => (
        <div className="p-multiselect-filter-container">
            <InputText
                value={filterValue}
                onChange={(e) => handleFilterInputChange(e.target.value)}
                className="p-multiselect-filter w-full"
                placeholder={th('common.search')}
                type="text"
                inputMode="text"
            />
            <span className="p-multiselect-filter-icon pi pi-search" />
        </div>
    );

    const handleChange = (e) => {
        const selected = e.value || [];
        onChange(selected);
    };

    const handleShow = () => {
        setFilterValue('');
        setSearchText('');
        
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
    };

    const handleHide = () => {
        setFilterValue('');
        setSearchText('');
        
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
    };

    const itemTemplate = (option) => {
        if (!option) return null;
        return showDescription ? (
            <div className="flex flex-column">
                <span className="font-medium">{option.id}</span>
                <small className="text-500">{option.rawText}</small>
            </div>
        ) : (
            <span className="font-medium">{option.id}</span>
        );
    };

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    return (
        <MultiSelect
            value={selectedChassisIds}
            onChange={handleChange}
            onShow={handleShow}
            onHide={handleHide}
            options={chassisOptions}
            optionLabel="id"
            optionValue="id"
            className={className}
            placeholder={placeholder || th('warrantyClaims.detail.form.chassisPlaceholder')}
            loading={isLoadingChassis}
            disabled={disabled}
            filter
            filterTemplate={filterTemplate}
            showClear
            display="chip"
            itemTemplate={itemTemplate}
            selectedItemsLabel={`{0} ${th('common.selected')}`}
            emptyFilterMessage={th('filter.noResultsFound')}
            virtualScrollerOptions={{ itemSize: 38 }}
             showSelectAll={false}  
        />
    );
};

export default ChassisMultiSelect;